import { Analytics } from './analytics.js';
import { TrustCategories } from './trust-categories.js';
import { Modal } from './modal.js';
import type { WidgetConfig } from './index.js';

export interface ProductData {
  success: boolean;
  product?: any;
  meta?: any;
  error?: string;
}

export interface BrandData {
  success: boolean;
  brand?: any;
}

enum AlertLevel {
  Good = 'good',
  Warn = 'warn', 
  Alert = 'alert',
  Neutral = 'neutral'
}

interface ColorsObject {
  text: string;
  bg: string;
  bgWash: string;
  hex: string;
}


// Alert colors mapping (used for CSS generation in theme variants)
const _AlertColors: Record<AlertLevel, ColorsObject> = {
  // Green
  [AlertLevel.Good]: {
    text: 'text-good',
    bg: 'bg-good',
    bgWash: 'bg-good/10',
    hex: '#31c48d'
  },
  // Orange
  [AlertLevel.Warn]: {
    text: 'text-warn',
    bg: 'bg-warn',
    bgWash: 'bg-warn/10',
    hex: '#FF8A4C'
  },
  // Red
  [AlertLevel.Alert]: {
    text: 'text-alert',
    bg: 'bg-alert',
    bgWash: 'bg-alert/10',
    hex: '#E02424'
  },
  // Gray
  [AlertLevel.Neutral]: {
    text: 'text-neutral',
    bg: 'bg-neutral',
    bgWash: 'bg-neutral/10',
    hex: '#9ca3af'
  }
};

export class SuppCoWidget {
  private element: HTMLElement;
  private productIdentifier: string; // Can be UPC or slug
  private metrics: Analytics;
  private data: ProductData | null = null;
  private brandData: BrandData | null = null;
  private trustCalculator: TrustCategories;
  private modal: Modal;
  private config: WidgetConfig;
  private preloadedIframe: HTMLIFrameElement | null = null;
  private isPreloadingIframe: boolean = false;
  
  // Track iframe widget instances across the page
  private static iframeWidgetCount = 0;
  private static fontsLoaded = false;

  constructor(element: HTMLElement, config: WidgetConfig) {
    if (config.debug) {
      console.log('🏗️ Creating widget for element:', element);
      console.log('🏗️ Element href:', (element as HTMLAnchorElement).href);
      console.log('🏗️ Element modal mode:', element.dataset.modalMode);
    }
    
    this.element = element;
    
    // Extract product identifier (UPC or slug) from URL
    let productIdentifier = '';
    if (element.tagName === 'A') {
      const anchorElement = element as HTMLAnchorElement;
      const extractedIdentifier = this.extractProductIdentifierFromURL(anchorElement.href);
      if (extractedIdentifier) {
        productIdentifier = extractedIdentifier;
        if (config.debug) {
          console.log('🔍 SuppCoWidget: Extracted product identifier:', productIdentifier);
        }
      }
    }
    
    this.productIdentifier = productIdentifier;
    this.config = config;
    this.metrics = new Analytics(config.version, config.debug);
    this.trustCalculator = new TrustCategories();
    this.modal = new Modal(config, this.metrics);
    
    // Load fonts globally if not already loaded
    this.loadFontsGlobally();
    
    // For anchor tags, prevent default click behavior during widget loading
    if (element.tagName === 'A') {
      this.setupAnchorBehavior(element as HTMLAnchorElement);
    }
    
    if (config.debug) {
      console.log('🚀 Initializing widget for product identifier:', this.productIdentifier);
    }
    this.init();
  }

  private loadFontsGlobally(): void {
    if (SuppCoWidget.fontsLoaded) return;
    
    // Only load once across all widget instances
    SuppCoWidget.fontsLoaded = true;
    
    // Add font CSS to document head if not already present
    if (!document.querySelector('#supp-co-fonts')) {
      const fontStyle = document.createElement('style');
      fontStyle.id = 'supp-co-fonts';
      fontStyle.textContent = `
        @font-face {
          font-family: 'Graphik';
          font-weight: 400;
          font-style: normal;
          font-display: swap;
          src: url('https://supp.co/fonts/Graphik-Regular-Web.woff2') format('woff2'),
               url('https://supp.co/fonts/Graphik-Regular-Web.woff') format('woff');
        }
        @font-face {
          font-family: 'Graphik';
          font-weight: 500;
          font-style: normal;
          font-display: swap;
          src: url('https://supp.co/fonts/Graphik-Medium-Web.woff2') format('woff2'),
               url('https://supp.co/fonts/Graphik-Medium-Web.woff') format('woff');
        }
      `;
      document.head.appendChild(fontStyle);
      if (this.config.debug) {
        console.log('🔤 Graphik fonts loaded globally');
      }
    }
    
    // Try to preload using CSS Font Loading API
    if ('fonts' in document && document.fonts.load) {
      Promise.all([
        document.fonts.load('400 16px "Graphik"'),
        document.fonts.load('500 16px "Graphik"')
      ]).then(() => {
        if (this.config.debug) {
          console.log('✅ Graphik fonts preloaded successfully');
        }
      }).catch((error) => {
        if (this.config.debug) {
          console.warn('⚠️ Font preloading failed:', error);
        }
      });
    }
  }

  // Extract product identifier (UPC or slug) from URL
  private extractProductIdentifierFromURL(url: string): string | null {
    if (!url) return null;
    
    try {
      // Support two formats:
      // 1. "https://supp.co/products/upc/[upc]" (8-14 digits)
      // 2. "https://supp.co/products/[slug]" (alphanumeric with hyphens)
      
      // Try UPC format first
      const upcPattern = /^https:\/\/supp\.co\/products\/upc\/(\d{8,14})$/;
      const upcMatch = url.match(upcPattern);
      if (upcMatch && this.isValidUPC(upcMatch[1])) {
        return upcMatch[1];
      }
      
      // Try slug format
      const slugPattern = /^https:\/\/supp\.co\/products\/([a-zA-Z0-9-]+)$/;
      const slugMatch = url.match(slugPattern);
      if (slugMatch && this.isValidSlug(slugMatch[1])) {
        return slugMatch[1];
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to extract product identifier from URL:', url, error);
      return null;
    }
  }

  // Validate UPC format (8-14 digits)
  private isValidUPC(upc: string): boolean {
    if (!upc) return false;
    return /^\d{8,14}$/.test(upc.trim());
  }

  // Validate slug format (alphanumeric with hyphens, at least 2 characters)
  private isValidSlug(slug: string): boolean {
    if (!slug || slug.length < 2) return false;
    return /^[a-zA-Z0-9-]+$/.test(slug.trim());
  }

  // Validate product identifier format (either UPC or slug)
  private isValidProductIdentifier(identifier: string): boolean {
    if (!identifier) return false;
    // Check if it's a valid UPC or slug
    return this.isValidUPC(identifier) || this.isValidSlug(identifier);
  }

  private setupAnchorBehavior(anchor: HTMLAnchorElement): void {
    // Store original text content for fallback
    const originalText = anchor.textContent?.trim();
    if (originalText) {
      anchor.dataset.originalText = originalText;
    }

    // Prevent default behavior while widget is loading/working
    anchor.addEventListener('click', (e) => {
      console.log('🔗 Anchor click handler triggered for product identifier:', this.productIdentifier);
      console.log('🔗 this.data?.product:', this.data?.product);
      
      // If widget is successfully loaded, let the widget handle clicks
      if (this.data?.product) {
        console.log('🔗 Anchor preventing default and opening modal');
        e.preventDefault();
        this.openModal();
      } else {
        console.log('🔗 Anchor allowing default link behavior (no product data)');
      }
      // If widget failed to load or is still loading, allow normal link behavior
    });

    // Remove default link styling since we're replacing with widget
    anchor.style.textDecoration = 'none';
    anchor.style.color = 'inherit';
    anchor.style.cursor = 'pointer';
  }

    private setupHoverPreloadingOnElement(targetElement: HTMLElement): void {
    // This method is used for iframe widgets #2+ on the page
    // The first iframe widget gets immediate preloading for best UX
    console.log('🔧 Setting up hover preloading for product identifier:', this.productIdentifier);
    
    // Skip on actual mobile/touch devices (not just small screens)
    const isTouchDevice = ('ontouchstart' in window) && 
                          (navigator.maxTouchPoints > 0) && 
                          (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    
    if (isTouchDevice) {
      console.log('📱 Skipping hover preload - actual mobile device detected');
      return;
    }
    
    // Respect user preferences for reduced motion
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      console.log('♿ Skipping hover preload - reduced motion preferred');
      return;
    }
    
    let hoverTimeout: number;
    
    console.log('👂 Adding mouseenter listener to:', targetElement.tagName, targetElement.className);
    console.log('👂 Element rect:', targetElement.getBoundingClientRect());
    
    // Add visual hover effect for debugging
    targetElement.style.transition = 'opacity 0.2s ease';
    
    // Test if ANY mouse events work
    targetElement.addEventListener('click', () => {
      console.log('🖱️ TEST: Click detected on hover element:', this.productIdentifier);
    });
    
    const mouseenterHandler = () => {
      console.log('🐭 Mouse entered widget:', this.productIdentifier);
      console.log('🐭 Event fired on element:', targetElement.tagName, targetElement.className);
      targetElement.style.opacity = '0.8'; // Visual feedback
      
      // Track hover event
      const widgetType = this.element.getAttribute('data-widget-type') || 'product';
      this.metrics.trackHover(this.productIdentifier, widgetType, {
        product_name: this.data?.product?.name || 'unknown',
        brand: this.data?.product?.brand || 'unknown'
      });
      
      // Debounce: only preload if hover lasts 200ms
      hoverTimeout = window.setTimeout(() => {
        console.log('⏰ Hover timeout reached, checking conditions...');
        console.log('🔍 this.isPreloadingIframe:', this.isPreloadingIframe);
        console.log('🔍 this.preloadedIframe:', this.preloadedIframe);
        
        // Check if we have an iframe that's off-screen (post-modal cleanup)
        const needsRepositioning = this.preloadedIframe && 
                                   this.preloadedIframe.style.top === '-9999px';
        
        if (!this.isPreloadingIframe && (!this.preloadedIframe || needsRepositioning)) {
          if (needsRepositioning) {
            console.log('🔄 Repositioning off-screen iframe for preload:', this.productIdentifier);
            this.repositionIframeForPreload();
          } else {
            console.log('🚀 Starting iframe preload for:', this.productIdentifier);
            this.isPreloadingIframe = true;
            
            // Track performance of hover preload
            const startTime = performance.now();
            this.preloadIframe();
            
            // Track preload performance after a delay
            setTimeout(() => {
              const duration = performance.now() - startTime;
              this.metrics.trackPerformance(this.productIdentifier, 'hover_preload', duration, {
                widget_type: widgetType,
                iframe_count: SuppCoWidget.iframeWidgetCount
              });
            }, 100);
          }
        } else {
          console.log('⏭️ Skipping preload - already preloading/preloaded');
          console.log('   - this.isPreloadingIframe:', this.isPreloadingIframe);
          console.log('   - this.preloadedIframe exists:', !!this.preloadedIframe);
        }
      }, 200);
    };
    
    targetElement.addEventListener('mouseenter', mouseenterHandler);
    console.log('✅ Mouseenter listener attached');
    
    targetElement.addEventListener('mouseleave', () => {
      console.log('🐭 Mouse left widget:', this.productIdentifier);
      console.log('🐭 Preloaded iframe still available?', !!this.preloadedIframe);
      targetElement.style.opacity = '1'; // Reset visual feedback
      clearTimeout(hoverTimeout);
    });
    
    console.log('✅ Hover event listeners attached for product identifier:', this.productIdentifier);
  }

  private repositionIframeForPreload(): void {
    if (!this.preloadedIframe) return;
    
    // Move iframe back to center position for preload
    this.preloadedIframe.style.position = 'fixed';
    this.preloadedIframe.style.top = '50%';
    this.preloadedIframe.style.left = '50%';
    this.preloadedIframe.style.transform = 'translate(-50%, -50%)';
    this.preloadedIframe.style.visibility = 'hidden'; // Hidden but positioned correctly
    this.preloadedIframe.style.zIndex = '-1';
    this.preloadedIframe.style.pointerEvents = 'none';
    
    console.log('✅ Repositioned existing iframe for preload');
  }

  private preloadIframe(): void {
    if (!this.productIdentifier) {
      console.warn('❌ Cannot preload iframe - no product identifier');
      return;
    }

    try {
      console.log('🔄 Preloading iframe on hover:', this.productIdentifier);
      
      // Create iframe with final modal dimensions but completely hidden
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.top = '50%';
      iframe.style.left = '50%';
      iframe.style.transform = 'translate(-50%, -50%)';
      iframe.style.width = '600px'; // Match modal width exactly
      iframe.style.height = '860px'; // Match modal height exactly
      iframe.style.maxWidth = '90vw';
      iframe.style.maxHeight = '90vh';
      iframe.style.border = 'none';
      iframe.style.borderRadius = '8px';
      iframe.style.visibility = 'hidden'; // Completely hidden but still loads content
      iframe.style.zIndex = '-1'; // Hide behind everything
      iframe.style.pointerEvents = 'none'; // No interaction
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups');
      iframe.setAttribute('data-preloaded', 'true'); // Mark as preloaded for cleanup
      
      // Build URL with parameters - MUST match modal URL exactly
      if (!this.data?.product?.slug) {
        console.warn('❌ Cannot preload iframe - no product slug available');
        return;
      }
      
      const url = new URL(`${this.config.domain}/products/${this.data.product.slug}/widget`);
      url.searchParams.set('widget_modal', 'true');
      url.searchParams.set('widget_version', this.config.version);
      url.searchParams.set('source_domain', window.location.hostname);
      url.searchParams.set('referrer', document.referrer || '');
      
      // Use same visit ID as main API call for consistency
      url.searchParams.set('visit_id', this.getOrCreateVisitId());
      
      const finalUrl = url.toString();
      console.log('🌐 Preload iframe URL:', finalUrl);
      iframe.src = finalUrl;
      
      // Track when iframe loads
      iframe.addEventListener('load', () => {
        console.log('✅ Iframe preloaded successfully:', this.productIdentifier);
        console.log('✅ Iframe element:', iframe);
        console.log('✅ Iframe in DOM:', document.body.contains(iframe));
        
        this.isPreloadingIframe = false; // Reset flag after successful load
        
        // Log preload metrics
        this.metrics.trackClick(this.productIdentifier, 'iframe_preload', {
          product_name: this.data?.product?.name || 'unknown',
          brand: this.data?.product?.brand || 'unknown',
          widget_type: this.element.getAttribute('data-widget-type') || 'product',
          iframe_count: SuppCoWidget.iframeWidgetCount
        });
      });
      
      iframe.addEventListener('error', () => {
        console.warn('❌ Iframe preload failed:', this.productIdentifier);
        this.isPreloadingIframe = false; // Reset flag after error
        // Clean up failed iframe
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
        this.preloadedIframe = null;
      });
      
      console.log('📎 Appending iframe to document.body');
      document.body.appendChild(iframe);
      this.preloadedIframe = iframe;
      console.log('✅ Iframe reference stored:', !!this.preloadedIframe);
      
    } catch (error) {
      console.warn('Iframe preload failed for product identifier', this.productIdentifier, ':', error);
    }
  }

  private getOrCreateVisitId(): string {
    let visitId = sessionStorage.getItem('supp_widget_visit');
    if (!visitId) {
      visitId = 'sw_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('supp_widget_visit', visitId);
    }
    return visitId;
  }

  private async init(): Promise<void> {
    if (this.config.debug) {
      console.log('🚀 Widget init() starting for product identifier:', this.productIdentifier);
    }
    
    if (!this.productIdentifier) {
      this.showError('Product identifier required');
      this.metrics.trackError('', 'no_product_identifier');
      return;
    }

    if (!this.isValidProductIdentifier(this.productIdentifier)) {
      this.showError('Invalid product identifier format');
      this.metrics.trackError(this.productIdentifier, 'invalid_product_identifier_format');
      return;
    }

    this.showLoading();

    try {
      // Context data to be base64 encoded
      const contextData = {
        // Core widget data  
        widget_version: this.config.version,
        widget_type: this.element.getAttribute('data-widget-type') || 'product',
        modal_mode: this.element.dataset.modalMode || 'iframe',
        
        // Page context
        source_domain: window.location.hostname,
        page_url: window.location.href,
        page_title: document.title,
        referrer: document.referrer || '',
        
        // Widget context
        original_href: (this.element as HTMLAnchorElement).href || '',
        rel_attribute: this.element.getAttribute('rel') || '',
        
        // Device context
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        
        // Visit identifier
        visit_id: this.getOrCreateVisitId(),
        timestamp: Date.now()
      };
      
      // Build clean API URL with base64-encoded context data
      const apiUrl = new URL(`${this.config.apiBase}/products/${this.productIdentifier}`);
      
      let response: Response;
      
      try {
        // Encode context data as base64 (looks like innocent parameter)
        const encodedData = btoa(JSON.stringify(contextData));
        apiUrl.searchParams.set('ctx', encodedData); // "ctx" = context, innocuous name
        
        if (this.config.debug) {
          console.log('🌐 Making API call to:', apiUrl.toString());
          console.log('📊 With encoded context data:', contextData);
        }
        
        response = await fetch(apiUrl.toString());
      } catch (encodingError) {
        if (this.config.debug) {
          console.warn('Base64 encoding failed, using clean GET:', encodingError);
        }
        
        // Fallback: Simple GET without any context data if encoding fails
        const cleanUrl = `${this.config.apiBase}/products/${this.productIdentifier}`;
        response = await fetch(cleanUrl);
        
        // Log the fallback
        this.metrics.trackError(this.productIdentifier, 'encoding_failed', {
          widget_type: this.element.getAttribute('data-widget-type') || 'product',
          fallback_used: true,
          error_message: encodingError instanceof Error ? encodingError.message : 'encoding_error'
        });
      }
      
      const data: ProductData = await response.json();

      if (data.success && data.product) {
        this.data = data;

        // Fetch brand trust score data if brand_id is available
        if (data.product.brand_id) {
          try {
            const brandResponse = await fetch(`${this.config.apiBase}/brands/${data.product.brand_id}`);
            const brandData: BrandData = await brandResponse.json();
            if (brandData.success) {
              this.brandData = brandData;
            }
          } catch (error) {
            if (this.config.debug) {
              console.warn('Failed to load brand data:', error);
            }
          }
        }

        this.render(data);
        
        // Client-side metrics now supplement server-side data
        this.metrics.trackImpression(this.productIdentifier, {
          product_name: data.product.name,
          brand: data.product.brand,
          widget_type: this.element.getAttribute('data-widget-type') || 'product',
          modal_mode: this.element.dataset.modalMode || 'iframe',
          trust_score_complete: data.product.product_trust_score_complete,
          trust_score: data.product.product_trust_score || data.product.brand_trust_score,
          server_data: 'supplemental' // Indicate this is supplemental to server data
        });
      } else {
        this.showError(data.error || 'Product not found');
        this.metrics.trackError(this.productIdentifier, data.error || 'product_not_found', {
          widget_type: this.element.getAttribute('data-widget-type') || 'product',
          api_response: data
        });
      }
    } catch (error) {
      console.error('SuppCo Widget: Failed to load product data for product identifier:', this.productIdentifier, error);
      this.showError('Failed to load product');
      this.metrics.trackError(this.productIdentifier, 'fetch_failed', {
        widget_type: this.element.getAttribute('data-widget-type') || 'product',
        error_message: error instanceof Error ? error.message : 'unknown_fetch_error'
      });
    }
  }

  private showLoading(): void {
    // Create wrapper div for Shadow DOM (anchor tags don't support Shadow DOM)
    const wrapper = document.createElement('div');
    wrapper.style.display = 'block';
    wrapper.style.width = '100%';
    
    // SEO-friendly content in light DOM  
    this.element.innerHTML = `<span class="sr-only">Loading product information...</span>`;
    this.element.appendChild(wrapper);

    // Create Shadow DOM on wrapper div
    const shadowRoot = wrapper.attachShadow({ mode: 'open' });
    
    // Add styles to shadow DOM
    const style = document.createElement('style');
    style.textContent = this.getWidgetCSS();
    shadowRoot.appendChild(style);

    // Create loading UI in shadow DOM
    const loadingContainer = document.createElement('div');
    loadingContainer.innerHTML = `
      <div class="supp-widget-loading">
        <div class="supp-spinner"></div>
        <p>Loading product...</p>
      </div>
    `;
    
    shadowRoot.appendChild(loadingContainer);
  }

  private showError(message: string): void {
    // Create wrapper div for Shadow DOM (anchor tags don't support Shadow DOM)
    const wrapper = document.createElement('div');
    wrapper.style.display = 'block';
    wrapper.style.width = '100%';
    
    // SEO-friendly content in light DOM
    this.element.innerHTML = `<span class="sr-only">Error: ${this.escapeHtml(message)}</span>`;
    this.element.appendChild(wrapper);

    // Create Shadow DOM on wrapper div
    const shadowRoot = wrapper.attachShadow({ mode: 'open' });
    
    // Add styles to shadow DOM
    const style = document.createElement('style');
    style.textContent = this.getWidgetCSS();
    shadowRoot.appendChild(style);

    // Create error UI in shadow DOM
    const errorContainer = document.createElement('div');
    errorContainer.innerHTML = `
      <div class="supp-widget-error">
        <p>${this.escapeHtml(message)}</p>
        <p style="font-size: 12px; margin-top: 8px;">
          <a href="${this.config.domain}/products/new?barcode=${this.productIdentifier}" 
             target="_blank" rel="noopener" style="color: #3b82f6; text-decoration: none;">
            Can't find this product? Add it to SuppCo →
          </a>
        </p>
      </div>
    `;
    
    shadowRoot.appendChild(errorContainer);
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  private formatPrice(price: string | number | null): string {
    if (!price || price === null) return 'Price not available';
    return '$' + parseFloat(String(price)).toFixed(2);
  }

  private formatTrustScore(score: string | number | null): string {
    if (!score && score !== 0) return 'N/A';
    const numericScore = typeof score === 'string' ? parseFloat(score) : score;
    if (isNaN(numericScore)) return 'N/A';
    return numericScore.toFixed(2);
  }

  private getTrustScoreData(product: any): { score: number | null, isError: boolean, errorMessage: string } {
    // Check if product trust score is complete
    if (product.product_trust_score_complete === true) {
      const score = product.product_trust_score || 0;
      return { score, isError: false, errorMessage: '' };
    } else {
      // Product trust score is not complete - show error state
      return { 
        score: null, 
        isError: true, 
        errorMessage: 'Trust analysis in progress' 
      };
    }
  }

  private getTrustScoreClass(state: string): string {
    if (!state) return AlertLevel.Neutral;
    const numericScore = parseFloat(state);
    
    // Use same thresholds as getProductTrustScoreAlertLabel from SuppCo
    if (numericScore >= 7) return AlertLevel.Good;     // Good, Very good, Excellent
    if (numericScore >= 6) return AlertLevel.Warn;     // Okay
    if (numericScore >= 0) return AlertLevel.Alert;    // Poor
    return AlertLevel.Neutral;  // Unknown
  }

  private getTrustScoreText(score: string | number, state: string): string {
    if (!score || !state) return 'Trust Score: N/A';
    return score + '% Trust Score';
  }

  // Get display score class for 10-point scale scores
  private getDisplayScoreClass(score: number): string {
    if (!score) return 'unknown';
    if (score >= 9.0) return 'excellent';
    if (score >= 8.0) return 'very-good';
    if (score >= 7.0) return 'good';
    if (score >= 6.0) return 'fair';
    if (score >= 4.0) return 'poor';
    return 'poor';
  }

  private render(data: ProductData): void {
    if (!data.product) return;
    
    const { product } = data;
    const widgetType = this.element.getAttribute('data-widget-type') || 'product';
    
    // Log trust score state
    const trustScoreData = this.getTrustScoreData(product);
    this.metrics.trackTrustScoreState(
      this.productIdentifier, 
      !trustScoreData.isError, 
      widgetType,
      {
        product_name: product.name,
        brand: product.brand,
        trust_score: trustScoreData.score,
        error_message: trustScoreData.errorMessage
      }
    );
    
    if (widgetType === 'badge') {
      this.renderBadge(product);
    } else if (widgetType === 'trust-info') {
      this.renderTrustInfo(product);
    } else {
      this.renderFullWidget(product);
    }
    
    // Smart preloading strategy for iframe widgets
    SuppCoWidget.iframeWidgetCount++;
    
    if (SuppCoWidget.iframeWidgetCount === 1) {
      // First iframe widget: preload immediately for best UX
      if (this.config.debug) {
        console.log('🚀 First iframe widget detected - preloading immediately for instant modal');
      }
      const startTime = performance.now();
      setTimeout(() => {
        if (!this.preloadedIframe && !this.isPreloadingIframe) {
          this.isPreloadingIframe = true;
          this.preloadIframe();
          
          // Track performance
          const duration = performance.now() - startTime;
          this.metrics.trackPerformance(this.productIdentifier, 'immediate_preload', duration, {
            widget_type: widgetType,
            iframe_count: SuppCoWidget.iframeWidgetCount
          });
        }
      }, 100); // Small delay to ensure widget is fully rendered
    } else {
      // Additional iframe widgets: use hover preloading
      if (this.config.debug) {
        console.log('📦 Additional iframe widget - setting up hover preloading');
      }
      this.setupHoverPreloadingOnElement(this.element);
    }
  }

  private renderFullWidget(product: any): void {
    const trustScoreData = this.getTrustScoreData(product);
    const formattedTrustScore = trustScoreData.isError ? 'N/A' : this.formatTrustScore(trustScoreData.score);
    const trustScoreClass = trustScoreData.isError ? AlertLevel.Neutral : this.getTrustScoreClass(trustScoreData.score?.toString() || '');
    const price = this.formatPrice(product.price);

    // Create wrapper div for Shadow DOM (anchor tags don't support Shadow DOM)
    const wrapper = document.createElement('div');
    wrapper.style.display = 'block';
    wrapper.style.width = '100%';
    
    // SEO-friendly content in light DOM
    this.element.innerHTML = `
      <span class="sr-only">
        ${this.escapeHtml(product.name || 'Unknown Product')} by ${this.escapeHtml(product.brand || 'Unknown Brand')} - 
        ${price} - ${trustScoreData.isError ? trustScoreData.errorMessage : formattedTrustScore + '% Trust Score'}
      </span>
    `;
    this.element.appendChild(wrapper);

    // Create Shadow DOM on wrapper div
    const shadowRoot = wrapper.attachShadow({ mode: 'open' });
    
    // Add styles to shadow DOM
    const style = document.createElement('style');
    style.textContent = this.getWidgetCSS();
    shadowRoot.appendChild(style);

    // Create widget in shadow DOM
    const widgetContainer = document.createElement('div');
    widgetContainer.innerHTML = `
      <div class="supp-widget">
        <div class="supp-widget-main" data-product-slug="${product.slug || ''}">
          <div class="supp-widget-image">
            <img src="${product.image_url || ''}" 
                 alt="${this.escapeHtml(product.name || 'Product')}" 
                 loading="lazy"
                 onerror="this.style.display='none'">
          </div>
          <div class="supp-widget-content">
            <h3 class="supp-widget-title">${this.escapeHtml(product.name || 'Unknown Product')}</h3>
            <p class="supp-widget-brand">${this.escapeHtml(product.brand || 'Unknown Brand')}</p>
            <div class="supp-widget-trust">
              <span class="supp-widget-score ${trustScoreClass}">
                ${trustScoreData.isError ? trustScoreData.errorMessage : formattedTrustScore + '% Trust Score'}
              </span>
            </div>
            <div class="supp-widget-price">
              ${price}
            </div>
            <button class="supp-widget-cta">View Details</button>
          </div>
        </div>
        <div class="supp-widget-footer">
          <a href="${this.config.domain}" target="_blank" rel="noopener">
            Powered by SuppCo
          </a>
        </div>
      </div>
    `;
    
    shadowRoot.appendChild(widgetContainer);

    // Add click handlers to shadow DOM elements
    const mainElement = shadowRoot.querySelector('.supp-widget-main');
    const ctaButton = shadowRoot.querySelector('.supp-widget-cta');

    const clickHandler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.openModal();
    };

    mainElement?.addEventListener('click', clickHandler);
    ctaButton?.addEventListener('click', clickHandler);
  }

  private renderBadge(product: any): void {
    const trustScoreData = this.getTrustScoreData(product);
    const formattedTrustScore = trustScoreData.isError ? 'N/A' : this.formatTrustScore(trustScoreData.score);
    const trustScoreClass = trustScoreData.isError ? AlertLevel.Neutral : this.getTrustScoreClass(trustScoreData.score?.toString() || '');

    // Create wrapper div for Shadow DOM (anchor tags don't support Shadow DOM)
    const wrapper = document.createElement('div');
    wrapper.style.display = 'inline-block';
    wrapper.style.verticalAlign = 'middle';
    
    // SEO-friendly content in light DOM
    this.element.innerHTML = `
      <span class="sr-only">${trustScoreData.isError ? trustScoreData.errorMessage : formattedTrustScore + '% Trust Score'}</span>
    `;
    this.element.appendChild(wrapper);

    // Create Shadow DOM on wrapper div
    const shadowRoot = wrapper.attachShadow({ mode: 'open' });
    
    // Add styles to shadow DOM
    const style = document.createElement('style');
    style.textContent = this.getBadgeCSS();
    shadowRoot.appendChild(style);

    // Create badge in shadow DOM
    const badgeContainer = document.createElement('div');
    badgeContainer.innerHTML = `
      <div class="supp-trust-badge" data-product-slug="${product.slug || ''}">
        <div class="supp-badge-content">
          <div class="supp-badge-score">
            <div class="supp-badge-dot ${trustScoreClass}"></div>
            <span class="supp-badge-number">${trustScoreData.isError ? '--' : formattedTrustScore}</span>
            <span class="supp-badge-label">${trustScoreData.isError ? 'Analyzing' : 'TrustScore™'}</span>
          </div>
        </div>
      </div>
    `;
    
    shadowRoot.appendChild(badgeContainer);

    // Add click handler to shadow DOM badge
    const badgeElement = shadowRoot.querySelector('.supp-trust-badge');
    
    const clickHandler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.openModal();
    };

    badgeElement?.addEventListener('click', clickHandler);
  }

  private renderTrustInfo(product: any): void {
    const trustScoreData = this.getTrustScoreData(product);
    const formattedTrustScore = trustScoreData.isError ? 'N/A' : this.formatTrustScore(trustScoreData.score);
    const trustScoreClass = trustScoreData.isError ? AlertLevel.Neutral : this.getTrustScoreClass(trustScoreData.score?.toString() || '');
    
    // Create wrapper div for Shadow DOM (anchor tags don't support Shadow DOM)
    const wrapper = document.createElement('div');
    wrapper.style.display = 'block';
    wrapper.style.width = '100%';
    
    // SEO-friendly content in light DOM
    this.element.innerHTML = `
      <span class="sr-only">
        ${this.escapeHtml(product.name || 'Unknown Product')} - 
        ${trustScoreData.isError ? trustScoreData.errorMessage : formattedTrustScore + '% Trust Score'} - 
        ${this.escapeHtml(product.brand || 'Unknown Brand')}
      </span>
    `;
    this.element.appendChild(wrapper);

    // Create Shadow DOM on wrapper div
    const shadowRoot = wrapper.attachShadow({ mode: 'open' });
    
    // Add styles to shadow DOM
    const style = document.createElement('style');
    style.textContent = this.getTrustInfoCSS();
    shadowRoot.appendChild(style);
    
    // Force font loading by creating a test element
    this.ensureFontLoading(shadowRoot);

    // Get all categories for display 
    const allCategories = this.getAllTrustCategories(product);
    
    // Create trust info widget in shadow DOM
    const trustInfoContainer = document.createElement('div');
    trustInfoContainer.innerHTML = `
      <div class="supp-trust-info" data-product-slug="${product.slug || ''}">
        <div class="supp-trust-info-header">
          <div class="supp-trust-info-left">
            <h3 class="supp-trust-info-title">Product TrustScore™</h3>
            <div class="supp-trust-info-verified">
              <div class="supp-trust-info-verified-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
  <path d="M2.93514 11.1372C2.41622 11.1372 2.02523 11.0074 1.76216 10.7479C1.4991 10.4883 1.36757 10.0953 1.36757 9.56895V8.51983C1.36757 8.45133 1.34414 8.39545 1.2973 8.35219L0.562162 7.61131C0.187387 7.23997 0 6.87044 0 6.5027C0 6.13497 0.187387 5.76363 0.562162 5.38869L1.2973 4.65322C1.34414 4.60635 1.36757 4.55047 1.36757 4.48558V3.43646C1.36757 2.90649 1.4973 2.51352 1.75676 2.25755C2.01982 1.99797 2.41261 1.86818 2.93514 1.86818H3.97838C4.04685 1.86818 4.1045 1.84295 4.15135 1.79247L4.89189 1.06242C5.26306 0.687472 5.63243 0.5 6 0.5C6.36757 0.5 6.73694 0.685669 7.10811 1.05701L7.84865 1.79247C7.8955 1.84295 7.95315 1.86818 8.02162 1.86818H9.06487C9.58739 1.86818 9.97838 1.99797 10.2378 2.25755C10.5009 2.51712 10.6324 2.91009 10.6324 3.43646V4.48558C10.6324 4.55047 10.6559 4.60635 10.7027 4.65322L11.4432 5.38869C11.8144 5.76363 12 6.13497 12 6.5027C12 6.87044 11.8144 7.23997 11.4432 7.61131L10.7027 8.35219C10.6559 8.39545 10.6324 8.45133 10.6324 8.51983V9.56895C10.6324 10.0917 10.5009 10.4829 10.2378 10.7425C9.97838 11.0056 9.58739 11.1372 9.06487 11.1372H8.02162C7.95315 11.1372 7.8955 11.1625 7.84865 11.2129L7.10811 11.9484C6.73694 12.3161 6.36757 12.5 6 12.5C5.63243 12.5 5.26306 12.3161 4.89189 11.9484L4.15135 11.2129C4.1045 11.1625 4.04685 11.1372 3.97838 11.1372H2.93514ZM5.44324 9.1201C5.71351 9.1201 5.93153 9.00113 6.0973 8.76318L8.44865 5.26431C8.5027 5.18499 8.54054 5.10748 8.56216 5.03177C8.58739 4.95246 8.6 4.87855 8.6 4.81005C8.6 4.61897 8.52793 4.46215 8.38378 4.33957C8.24324 4.21338 8.07928 4.15029 7.89189 4.15029C7.65045 4.15029 7.44685 4.28008 7.28108 4.53966L5.44324 7.43285L4.67027 6.54056C4.59459 6.45764 4.51351 6.39455 4.42703 6.35128C4.34414 6.30442 4.24865 6.28098 4.14054 6.28098C3.95315 6.28098 3.78919 6.34768 3.64865 6.48107C3.51171 6.61447 3.44324 6.7749 3.44324 6.96237C3.44324 7.13542 3.50991 7.30126 3.64324 7.45989L4.77297 8.77941C4.86667 8.88756 4.96757 8.97229 5.07568 9.03357C5.18739 9.09126 5.30991 9.1201 5.44324 9.1201Z" fill="#0098C5"/>
</svg></div>
              <span class="supp-trust-info-verified-text">${trustScoreData.isError ? 'Analysis in progress' : 'Verified by SuppCo'}</span>
            </div>
          </div>
          <div class="supp-trust-info-right">
            <div class="supp-trust-info-score-badge ${trustScoreClass}">
              <div class="supp-trust-info-score-content">
                <span class="supp-trust-info-score-number">${trustScoreData.isError ? '--' : formattedTrustScore}</span>
                <span class="supp-trust-info-score-label">${trustScoreData.isError ? 'Analyzing' : this.getTrustRatingLabel(trustScoreData.score || 0)}</span>
              </div>
              <img class="supp-trust-info-product-image" 
                   src="${product.image_url || 'https://placehold.co/37x37'}" 
                   alt="${this.escapeHtml(product.name || 'Product')}" 
                   loading="lazy"
                   onerror="this.src='https://placehold.co/37x37'">
            </div>
          </div>
        </div>
        
        <div class="supp-trust-info-categories">
          ${trustScoreData.isError ? `
            <div class="supp-trust-info-category">
              <span class="supp-trust-info-cat-name">Analysis Status</span>
              <div class="supp-trust-info-cat-right">
                <span class="supp-trust-info-cat-value">IN PROGRESS</span>
                <div class="supp-trust-info-status-dot ${AlertLevel.Warn}"></div>
              </div>
            </div>
          ` : allCategories.slice(0, 4).map((cat: {label: string, value: string, class: string}) => `
            <div class="supp-trust-info-category">
              <span class="supp-trust-info-cat-name">${cat.label}</span>
              <div class="supp-trust-info-cat-right">
                <span class="supp-trust-info-cat-value">${cat.value}</span>
                <div class="supp-trust-info-status-dot ${cat.class}"></div>
              </div>
            </div>
          `).join('')}
          ${!trustScoreData.isError && allCategories.length > 4 ? `
            <div class="supp-trust-info-category faded">
              <span class="supp-trust-info-cat-name">${allCategories[4].label}</span>
              <div class="supp-trust-info-cat-right">
                <span class="supp-trust-info-cat-value">${allCategories[4].value}</span>
                <div class="supp-trust-info-status-dot ${allCategories[4].class}"></div>
              </div>
            </div>
          ` : ''}
        </div>
        
        <div class="supp-trust-info-overlay">
          <div class="supp-trust-info-see-all-btn">
            ${trustScoreData.isError ? 'Check status' : 'See all categories'}
          </div>
        </div>
        
        <div class="supp-trust-info-footer">
          <span class="supp-trust-info-footer-text">${trustScoreData.isError ? 'Analysis in progress' : 'View full TrustScore details'}</span>
          <div class="supp-trust-info-logo">
            <svg xmlns="http://www.w3.org/2000/svg" width="58" height="15" viewBox="0 0 58 15" fill="none">
  <path d="M13.8613 11.5329C11.5178 11.5329 10.1037 10.255 10.0498 8.40474H11.6121C11.6929 9.50957 12.5549 10.2417 13.8748 10.2417C14.9927 10.2417 15.8008 9.74917 15.8008 8.85732C15.8008 7.87229 14.8176 7.65931 13.3226 7.39309C11.7603 7.12686 10.2788 6.63435 10.2788 4.78409C10.2788 3.16013 11.6256 2.0553 13.6324 2.0553C15.7873 2.0553 17.1341 3.21337 17.2015 4.93052H15.6526C15.5314 3.98542 14.7772 3.34648 13.6324 3.34648C12.5684 3.34648 11.8276 3.79906 11.8276 4.6776C11.8276 5.63601 12.7973 5.83568 14.2788 6.08859C15.8412 6.36813 17.3496 6.87395 17.3496 8.69759C17.3496 10.3748 15.9354 11.5329 13.8613 11.5329Z" fill="black"/>
  <path d="M18.1756 8.43136V4.63767H19.6301V8.27163C19.6301 9.60275 20.1689 10.2816 21.2329 10.2816C22.3373 10.2816 23.0107 9.45632 23.0107 8.04534V4.63767H24.4653V11.453H23.1858L23.0241 10.5878C22.5932 11.1069 21.9602 11.5329 20.8962 11.5329C19.4281 11.5329 18.1756 10.7608 18.1756 8.43136Z" fill="black"/>
  <path d="M29.3074 4.5578C31.1929 4.5578 32.5667 5.8623 32.5667 8.04534C32.5667 10.1485 31.1929 11.5329 29.3074 11.5329C28.2434 11.5329 27.5026 11.1069 27.0717 10.4946V14.1152H25.6171V4.63767H26.8831L27.0582 5.64932C27.5161 5.05032 28.2299 4.5578 29.3074 4.5578ZM29.065 5.78243C27.8528 5.78243 27.0582 6.70091 27.0582 8.01871C27.0582 9.37646 27.8528 10.3216 29.065 10.3216C30.2771 10.3216 31.0852 9.37646 31.0852 8.04534C31.0852 6.70091 30.2771 5.78243 29.065 5.78243Z" fill="black"/>
  <path d="M37.141 4.5578C39.0265 4.5578 40.4003 5.8623 40.4003 8.04534C40.4003 10.1485 39.0265 11.5329 37.141 11.5329C36.077 11.5329 35.3362 11.1069 34.9052 10.4946V14.1152H33.4506V4.63767H34.7167L34.8918 5.64932C35.3497 5.05032 36.0635 4.5578 37.141 4.5578ZM36.8985 5.78243C35.6864 5.78243 34.8918 6.70091 34.8918 8.01871C34.8918 9.37646 35.6864 10.3216 36.8985 10.3216C38.1107 10.3216 38.9188 9.37646 38.9188 8.04534C38.9188 6.70091 38.1107 5.78243 36.8985 5.78243Z" fill="black"/>
  <path d="M45.5267 11.5329C42.7792 11.5329 41.0687 9.65599 41.0687 6.79408C41.0687 3.9588 42.8331 2.0553 45.621 2.0553C47.7759 2.0553 49.3248 3.33317 49.6345 5.30323H48.0318C47.722 4.13184 46.8331 3.39973 45.5537 3.39973C43.7624 3.39973 42.658 4.7974 42.658 6.79408C42.658 8.77745 43.7354 10.1884 45.5133 10.1884C46.8197 10.1884 47.722 9.46964 48.0184 8.28494H49.6211C49.3248 10.255 47.722 11.5329 45.5267 11.5329Z" fill="black"/>
  <path d="M53.7318 10.3082C54.9574 10.3082 55.7655 9.36315 55.7655 8.04534C55.7655 6.72753 54.9574 5.78243 53.7318 5.78243C52.5062 5.78243 51.7116 6.72753 51.7116 8.04534C51.7116 9.36315 52.5062 10.3082 53.7318 10.3082ZM53.7318 11.5329C51.6308 11.5329 50.2166 10.1352 50.2166 8.04534C50.2166 5.96879 51.6308 4.5578 53.7318 4.5578C55.8329 4.5578 57.247 5.96879 57.247 8.04534C57.247 10.1352 55.8329 11.5329 53.7318 11.5329Z" fill="black"/>
  <path d="M2.83885 5.76798L3.77823 6.09842C4.80396 6.46095 5.62512 6.84055 6.26859 7.25633V3.02274C6.26859 1.3603 4.85847 0.000305176 3.13404 0.000305176C1.7041 0.000305176 0.490065 0.937007 0.118421 2.2062C0.0724082 2.40214 0.0483398 2.60696 0.0483398 2.81792C0.0483398 4.20727 1.08045 5.1508 2.83885 5.76798Z" fill="#25AAD1"/>
  <path d="M3.19967 8.14897L2.28437 7.81853C1.41154 7.50516 0.637103 7.14126 0 6.70227V10.9775C0 12.6399 1.41012 13.9999 3.13384 13.9999C4.15037 13.9999 5.05789 13.5268 5.63129 12.7977C6.02417 12.2754 6.2337 11.6432 6.2337 10.972C6.2337 9.539 5.29433 8.89929 3.19897 8.14965L3.19967 8.14897Z" fill="#AC94FA"/>
</svg>
          </div>
        </div>
      </div>
    `;
    
    shadowRoot.appendChild(trustInfoContainer);

    // Add click handler to shadow DOM trust info widget
    const trustInfoElement = shadowRoot.querySelector('.supp-trust-info');
    
    const clickHandler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.openModal();
    };

    trustInfoElement?.addEventListener('click', clickHandler);
  }

  // copied from suppco
  private getTrustScoreAlertLabel(score: number): string {
    if (score >= 8.6) {
      return 'Excellent';
    } else if (score >= 8.1) {
      return 'Very good';
    } else if (score >= 7.6) {
      return 'Good';
    } else if (score >= 7.1) {
      return 'Average';
    } else {
      return 'Reconsider';
    }
  }
  
  // copied from suppco
  private getProductTrustScoreAlertLabel(score: number): string {
    if (score >= 9) {
      return 'Excellent';
    } else if (score >= 8) {
      return 'Very good';
    } else if (score >= 7) {
      return 'Good';
    } else if (score >= 6) {
      return 'Okay';
    } else {
      return 'Poor';
    }
  }

  private getTrustRatingLabel(score: number): string {
    return this.getProductTrustScoreAlertLabel(score);
  }


  private getTopTrustCategories(_product: any): Array<{label: string, value: string, class: string}> {
    // Get real trust categories from the trust calculator
    const trustCategories = this.trustCalculator.getTrustCategories(this.data, this.brandData);
    
    // Convert to display format and prioritize most important categories
    const displayCategories: Array<{label: string, value: string, class: string, priority: number}> = [];
    
    trustCategories.forEach((category) => {
      let priority = 0;
      let shortLabel = category.name;
      
      // Set priority and short labels for display
      switch (category.name) {
        case 'Manufacturing Standards':
          priority = 5; // High priority
          shortLabel = 'Manufacturing';
          break;
        case 'Brand Certifications':
          priority = 4;
          shortLabel = 'Certifications';
          break;
        case 'Product Quality':
          priority = 4;
          shortLabel = 'Quality';
          break;
        case 'Testing Benchmarks':
          priority = 3;
          shortLabel = 'Testing';
          break;
        case 'Product Certifications':
          priority = 3;
          shortLabel = 'Product Certs';
          break;
        case 'Technical Innovation':
          priority = 2;
          shortLabel = 'Innovation';
          break;
        case 'Inactive ingredients':
          priority = 1; // Lower priority for widget display
          shortLabel = 'Ingredients';
          break;
        default:
          priority = 1;
      }
      
      // Map evaluation class to our widget classes
      let widgetClass = AlertLevel.Neutral;
      switch (category.evaluation.class) {
        case 'high':
        case 'safe':
          widgetClass = AlertLevel.Good;
          break;
        case 'ok':
        case 'warning':
          widgetClass = AlertLevel.Warn;
          break;
        case 'poor':
        case 'danger':
          widgetClass = AlertLevel.Alert;
          break;
        case 'unknown':
        default:
          widgetClass = AlertLevel.Neutral;
      }
      
      displayCategories.push({
        label: shortLabel,
        value: category.evaluation.text,
        class: widgetClass,
        priority
      });
    });
    
         // Sort by priority (highest first) and return top 3
     const sortedCategories = displayCategories
       .sort((a, b) => b.priority - a.priority)
       .slice(0, 3)
       .map(({ label, value, class: widgetClass }) => ({ label, value, class: widgetClass }));
     
     // If no categories available, show placeholder data
     if (sortedCategories.length === 0) {
       return [
         { label: 'Manufacturing', value: 'UNKNOWN', class: AlertLevel.Neutral },
         { label: 'Certifications', value: 'UNKNOWN', class: AlertLevel.Neutral },
         { label: 'Testing', value: 'UNKNOWN', class: AlertLevel.Neutral }
       ];
     }
     
          return sortedCategories;
   }

  private getAllTrustCategories(_product: any): Array<{label: string, value: string, class: string}> {
    // Get real trust categories from the trust calculator
    const trustCategories = this.trustCalculator.getTrustCategories(this.data, this.brandData);
    
    // Convert to display format
    const displayCategories: Array<{label: string, value: string, class: string}> = [];
    
    trustCategories.forEach((category) => {
      let shortLabel = category.name;
      
      // Set short labels for display
      switch (category.name) {
        case 'Manufacturing Standards':
          shortLabel = 'Manufacturing Standards';
          break;
        case 'Brand Certifications':
          shortLabel = 'Brand certifications';
          break;
        case 'Product Quality':
          shortLabel = 'Product quality';
          break;
        case 'Testing Benchmarks':
          shortLabel = 'Testing benchmarks';
          break;
        case 'Product Certifications':
          shortLabel = 'Product certifications';
          break;
        case 'Technical Innovation':
          shortLabel = 'Technical innovations';
          break;
        case 'Inactive ingredients':
          shortLabel = 'Inactive ingredients';
          break;
        default:
          shortLabel = category.name;
      }
      
      // Map evaluation class to our widget classes
      let widgetClass = AlertLevel.Neutral;
      switch (category.evaluation.class) {
        case 'high':
        case 'safe':
          widgetClass = AlertLevel.Good;
          break;
        case 'ok':
        case 'warning':
          widgetClass = AlertLevel.Warn;
          break;
        case 'poor':
        case 'danger':
          widgetClass = AlertLevel.Alert;
          break;
        case 'unknown':
        default:
          widgetClass = AlertLevel.Neutral;
      }
      
      displayCategories.push({
        label: shortLabel,
        value: category.evaluation.text,
        class: widgetClass
      });
    });
    
    // If no categories available, show placeholder data
    if (displayCategories.length === 0) {
      return [
        { label: 'Manufacturing Standards', value: 'UNKNOWN', class: AlertLevel.Neutral },
        { label: 'Brand certifications', value: 'UNKNOWN', class: AlertLevel.Neutral },
        { label: 'Testing benchmarks', value: 'UNKNOWN', class: AlertLevel.Neutral },
        { label: 'Product quality', value: 'UNKNOWN', class: AlertLevel.Neutral },
        { label: 'Product certifications', value: 'UNKNOWN', class: AlertLevel.Neutral },
        { label: 'Technical innovations', value: 'UNKNOWN', class: AlertLevel.Neutral }
      ];
    }
    
    return displayCategories;
  }

  private ensureFontLoading(shadowRoot: ShadowRoot): void {
    // Create a hidden test element to force font loading
    const fontTest = document.createElement('div');
    fontTest.style.fontFamily = "'Graphik', Arial, sans-serif";
    fontTest.style.position = 'absolute';
    fontTest.style.visibility = 'hidden';
    fontTest.style.fontSize = '16px';
    fontTest.textContent = 'Font Loading Test';
    shadowRoot.appendChild(fontTest);
    
    // Use document.fonts API if available to load fonts explicitly
    if ('fonts' in document && document.fonts.load) {
      document.fonts.load('16px "Graphik"').then(() => {
        console.log('✅ Graphik font loaded successfully');
      }).catch((error) => {
        console.warn('⚠️ Graphik font failed to load:', error);
      });
    }
    
    // Remove test element after a short delay
    setTimeout(() => {
      if (fontTest.parentNode) {
        fontTest.parentNode.removeChild(fontTest);
      }
    }, 100);
  }

  private getTrustInfoCSS(): string {
    return `
      /* SuppCo Graphik Font - Self-hosted with fallbacks */
      @font-face {
        font-family: 'Graphik';
        font-weight: 400;
        font-style: normal;
        font-display: swap;
        src: url('https://supp.co/fonts/Graphik-Regular-Web.woff2') format('woff2'),
             url('https://supp.co/fonts/Graphik-Regular-Web.woff') format('woff');
      }

      @font-face {
        font-family: 'Graphik';
        font-weight: 500;
        font-style: normal;
        font-display: swap;
        src: url('https://supp.co/fonts/Graphik-Medium-Web.woff2') format('woff2'),
             url('https://supp.co/fonts/Graphik-Medium-Web.woff') format('woff');
      }

      /* Trust Info Widget Styles - Shadow DOM */
      :host {
        font-family: var(--supp-widget-font-family, 'Graphik', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif);
        font-size: 16px; /* Base font size - prevents inheritance from parent */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      
      .supp-trust-info {
        font-family: 'Graphik', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        background: white;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
        border: 1px solid #e5e7eb;
        padding: 17px;
        width: auto;
        max-width: 400px;
        position: relative;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .supp-trust-info:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
      }

      /* Header */
      .supp-trust-info-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 16px;
      }

      .supp-trust-info-left {
        flex: 1;
      }

      .supp-trust-info-title {
        font-size: 16px;
        font-weight: 500;
        color: #111827;
        margin: 0 0 4px 0;
        line-height: normal;
      }

      .supp-trust-info-verified {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .supp-trust-info-verified-icon {
        //width: 16px;
        //height: 16px;
        //background: #3b82f6;
        border-radius: 2px;
        flex-shrink: 0;
      }

      .supp-trust-info-verified-text {
        color: var(--Text-text-blue-default, #0A7594);
        leading-trim: both;
        text-edge: cap;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 120%;
      }

      .supp-trust-info-right {
        margin-left: 8px;
      }

      .supp-trust-info-score-badge {
        display: flex;
        padding: 8px 6px 8px 12px;
        justify-content: center;
        align-items: center;
        gap: 16px;
        border-radius: 12px;
      }

      .supp-trust-info-score-badge.good {
        background: #31c48d;
      }

      .supp-trust-info-score-badge.warn {
        background: #FF8A4C;
      }

      .supp-trust-info-score-badge.alert {
        background: #E02424;
      }

      .supp-trust-info-score-badge.neutral {
        background: #D1D5DB;
      }

      .supp-trust-info-score-content {
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
      }

      .supp-trust-info-score-number {
        color: #FFF;
        text-align: center;
        leading-trim: both;
        text-edge: cap;
        font-feature-settings: 'case' on;
        font-family: Graphik;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: 129%;
      }

      .supp-trust-info-score-label {
        color: #FFF;
        text-align: center;
        leading-trim: both;
        text-edge: cap;
        font-feature-settings: 'case' on;
        font-family: Graphik;
        font-size: 8px;
        font-style: normal;
        font-weight: 600;
        line-height: 129%;
        text-transform: uppercase;
      }

      .supp-trust-info-product-image {
        width: 37px;
        height: 37px;
        flex-shrink: 0;
        border-radius: 8px;
        object-fit: cover;
        background: lightgray 50% / cover no-repeat;
      }

      /* Categories */
      .supp-trust-info-categories {
        margin-bottom: 12px;
        position: relative;
      }

      .supp-trust-info-category {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 0;
        border-bottom: 1px solid #f3f4f6;
      }

      .supp-trust-info-category:last-child {
        border-bottom: none;
      }

      .supp-trust-info-category.faded {
        opacity: 0.4;
      }

      .supp-trust-info-cat-name {
        color: var(--vital-primary, #111928);
        font-feature-settings: 'case' on;
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
        letter-spacing: -0.34px;
      }

      .supp-trust-info-cat-right {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .supp-trust-info-cat-value {
        color: var(--vital-secondary, #6B7280);
        font-feature-settings: 'case' on;
        font-family: Graphik;
        font-size: 11px;
        font-style: normal;
        font-weight: 500;
        line-height: 130%;
        text-transform: uppercase;
      }

      .supp-trust-info-status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .supp-trust-info-status-dot.good {
        background: #31c48d;
      }

      .supp-trust-info-status-dot.warn {
        background: #FF8A4C;
      }

      .supp-trust-info-status-dot.alert {
        background: #E02424;
      }

      .supp-trust-info-status-dot.neutral {
        background: #D1D5DB;
      }

      /* Overlay */
      .supp-trust-info-overlay {
        position: relative;
        margin-bottom: 12px;
        display: flex;
        justify-content: center;
      }

      .supp-trust-info-overlay::before {
        content: '';
        position: absolute;
        top: -20px;
        left: -12px;
        right: -12px;
        height: 30px;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 50%, white 100%);
        pointer-events: none;
      }

      .supp-trust-info-see-all-btn {
        display: flex;
        background: white;
        border: 1px solid var(--Border-border-gray-lighter, #E5E7EB);
        border-radius: 100px;
        background: var(--Background-background-white, #FFF);
        padding: 10px;
        font-size: 14px;
        font-weight: 500;
        color: #374151;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
        z-index: 1;
        width: 161px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
      }

      .supp-trust-info-see-all-btn:hover {
        background: #f9fafb;
        border-color: #9ca3af;
      }

      /* Footer */
      .supp-trust-info-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .supp-trust-info-footer-text {
        color: var(--Text-text-blue-default, #0A7594);
        font-feature-settings: 'case' on;
        font-family: Graphik;
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
        letter-spacing: -0.34px;
      }

      .supp-trust-info-logo {
        width: 58px;
        //height: 14px;
      }

      /* Responsive */
      @media (max-width: 480px) {
        .supp-trust-info {
          padding: 12px;
        }

        .supp-trust-info-header {
          flex-direction: row;
          align-items: flex-start;
          gap: 4px;
        }

        .supp-trust-info-right {
          margin-left: 0;
        }

        .supp-trust-info-title {
          font-size: 20px;
        }
      }

      /* Accessibility */
      .supp-trust-info:focus-visible {
        outline: 2px solid #3b82f6;
        outline-offset: 2px;
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .supp-trust-info {
          transition: none;
        }

        .supp-trust-info:hover {
          transform: none;
        }
      }
    `;
  }

  private getWidgetCSS(): string {
    return `
      /* SuppCo Graphik Font - Self-hosted */
      @font-face {
        font-family: Graphik;
        font-weight: 400;
        font-display: block;
        src: url(https://supp.co/fonts/Graphik-Regular-Web.woff2);
      }

      @font-face {
        font-family: Graphik;
        font-weight: 500;
        font-display: block;
        src: url(https://supp.co/fonts/Graphik-Medium-Web.woff2);
      }
      
      /* SuppCo Widget Styles - Shadow DOM */
      :host {
        display: block;
        /* Inherit custom font from parent document if provided, fallback to Graphik */
        font-family: var(--supp-widget-font-family, 'Graphik', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif);
        font-size: 16px; /* Base font size - prevents inheritance from parent */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      .supp-widget {
        font-family: inherit; /* Inherit from :host */
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        overflow: hidden;
        max-width: 400px;
        background: white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        position: relative;
        display: block;
      }

      .supp-widget:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .supp-widget-main {
        padding: 16px;
        display: flex;
        gap: 12px;
        align-items: flex-start;
      }

      .supp-widget-image {
        flex-shrink: 0;
      }

      .supp-widget-image img {
        width: 80px;
        height: 80px;
        object-fit: cover;
        border-radius: 8px;
        background: #f3f4f6;
      }

      .supp-widget-content {
        flex: 1;
        min-width: 0;
      }

      .supp-widget-title {
        font-size: 16px;
        font-weight: 500;
        margin: 0 0 4px 0;
        color: #111827;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .supp-widget-brand {
        font-size: 14px;
        color: #6b7280;
        margin: 0 0 8px 0;
        font-weight: 500;
      }

      .supp-widget-trust {
        margin: 0 0 8px 0;
      }

      .supp-widget-score {
        font-size: 12px;
        padding: 4px 8px;
        border-radius: 6px;
        font-weight: 500;
        display: inline-block;
      }

      .supp-widget-score.good {
        background: rgba(58, 217, 168, 0.1);
        color: #166534;
      }

      .supp-widget-score.warn {
        background: rgba(255, 138, 76, 0.1);
        color: #92400e;
      }

      .supp-widget-score.alert {
        background: rgba(224, 36, 36, 0.1);
        color: #991b1b;
      }

      .supp-widget-score.neutral {
        background: rgba(209, 213, 219, 0.1);
        color: #6b7280;
      }

      .supp-widget-price {
        font-size: 16px;
        font-weight: 500;
        color: #059669;
        margin: 4px 0 0 0;
      }

      .supp-widget-cta {
        background: #3b82f6;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s ease;
        margin-top: 8px;
      }

      .supp-widget-cta:hover {
        background: #2563eb;
      }

      .supp-widget-footer {
        background: #f9fafb;
        padding: 8px 16px;
        text-align: center;
        border-top: 1px solid #f3f4f6;
      }

      .supp-widget-footer a {
        color: #6b7280;
        text-decoration: none;
        font-size: 12px;
        font-weight: 500;
      }

      .supp-widget-footer a:hover {
        color: #374151;
      }

      /* Loading & Error States */
      .supp-widget-loading,
      .supp-widget-error {
        padding: 24px;
        text-align: center;
      }

      .supp-widget-loading p,
      .supp-widget-error p {
        margin: 12px 0 0 0;
        color: #6b7280;
        font-size: 14px;
      }

      .supp-spinner {
        width: 24px;
        height: 24px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #3b82f6;
        border-radius: 50%;
        animation: supp-spin 1s linear infinite;
        margin: 0 auto;
      }

      @keyframes supp-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      .supp-widget-error {
        color: #ef4444;
      }

      /* Responsive */
      @media (max-width: 480px) {
        .supp-widget {
          max-width: 100%;
        }

        .supp-widget-main {
          padding: 12px;
          gap: 10px;
        }

        .supp-widget-image img {
          width: 60px;
          height: 60px;
        }

        .supp-widget-title {
          font-size: 14px;
        }

        .supp-widget-brand {
          font-size: 12px;
        }
      }

      /* Accessibility */
      .supp-widget:focus-visible {
        outline: 2px solid #3b82f6;
        outline-offset: 2px;
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .supp-widget,
        .supp-widget-cta,
        .supp-spinner {
          transition: none;
          animation: none;
        }

        .supp-widget:hover {
          transform: none;
        }
      }
    `;
  }

  private getBadgeCSS(): string {
    return `
      /* SuppCo Graphik Font - Self-hosted */
      @font-face {
        font-family: Graphik;
        font-weight: 400;
        font-display: block;
        src: url(https://supp.co/fonts/Graphik-Regular-Web.woff2);
      }

      @font-face {
        font-family: Graphik;
        font-weight: 500;
        font-display: block;
        src: url(https://supp.co/fonts/Graphik-Medium-Web.woff2);
      }
      
      /* Trust Badge Widget Styles - Shadow DOM */
      :host {
        /* Inherit custom font from parent document if provided, fallback to Graphik */
        font-family: var(--supp-widget-font-family, 'Graphik', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif);
        font-size: 16px; /* Base font size - prevents inheritance from parent */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      
      .supp-trust-badge {
        font-family: inherit; /* Inherit from :host */
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s ease;
        text-decoration: none;
        position: relative;
      }

      .supp-trust-badge:hover {
        transform: translateY(-1px);
      }

      .supp-badge-content {
        display: flex;
        align-items: center;
        gap: 2px;
      }

      .supp-badge-score {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border-radius: 8px;
        border: 1px solid #ECECEC;
        font-weight: 600;
        font-size: 14px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;
        background: white;
      }

      .supp-badge-score:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }

      .supp-badge-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }

      .supp-badge-dot.good {
        background: #31c48d;
        color: white;
      }

      .supp-badge-dot.warn {
        background: #FF8A4C;
        color: white;
      }

      .supp-badge-dot.alert {
        background: #E02424;
        color: white;
      }

      .supp-badge-dot.neutral {
        background: #D1D5DB;
        color: white;
      }

      .supp-badge-number {
        color: #111928;
        text-align: center;
        font-feature-settings: 'case' on;
        font-family: Graphik;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }

      .supp-badge-label {
        color: var(--Content-content-gray-default, #687280);
        text-align: center;
        font-feature-settings: 'case' on;
        font-family: Graphik;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }

      /* Responsive */
      @media (max-width: 480px) {
        .supp-badge-score {
          padding: 6px 10px;
          font-size: 12px;
          gap: 4px;
        }

        .supp-badge-number {
          font-size: 14px;
        }

        .supp-badge-label {
          font-size: 11px;
        }
      }

      /* Accessibility */
      .supp-trust-badge:focus-visible {
        outline: 2px solid #3b82f6;
        outline-offset: 2px;
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .supp-trust-badge,
        .supp-badge-score {
          transition: none;
        }

        .supp-trust-badge:hover {
          transform: none;
        }
      }
    `;
  }

  private openModal(): void {
    if (!this.data?.product) return;

    this.metrics.trackModalOpen(this.productIdentifier, {
      product_name: this.data.product.name,
      brand: this.data.product.brand,
      widget_type: this.element.getAttribute('data-widget-type') || 'product',
      modal_mode: 'iframe',
      trust_score_complete: this.data.product.product_trust_score_complete,
      trust_score: this.data.product.product_trust_score || this.data.product.brand_trust_score,
      preloaded_iframe_available: !!this.preloadedIframe
    });

    // Calculate trust categories for the modal
    const trustCategories = this.trustCalculator.getTrustCategories(this.data, this.brandData);

    if (this.config.debug) {
      console.log('🚪 Opening iframe modal');
      console.log('🚪 Preloaded iframe available:', !!this.preloadedIframe);
    }

    // Use preloaded iframe if available (simplified)
    this.modal.open(this.data, this.brandData, trustCategories, this.productIdentifier, this.preloadedIframe);
    
    // Keep preloaded iframe reference for potential reuse
    // Modal will handle moving it off-screen, we can reuse it later
  }
} 